import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, CloseIcon, createIcon } from '@blade/ui-kit'
import { boostersFontCss, premiumFontCss } from '@root/entities/premium/ui'
import { useAppRoutes } from '@root/features/routing'
import { PausedBadge } from '@root/shared/ui-kit'

import { premiumFeedTableModel } from '../../atoms/reatom-premium-feed-table'

type Props = {
  onClose?: () => void
}

export const PremiumFeedDialogHeader: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()
  const [paused] = useAtom(premiumFeedTableModel.paused)
  const { navigateToPremiumIndex } = useAppRoutes()

  return (
    <Root>
      <HeaderStart>
        <Title>
          <PremiumFilledIcon size={22} />
          <span>{t('premium.feed')}</span>
        </Title>

        {paused ? <PausedBadge paused={paused} /> : undefined}
      </HeaderStart>
      <HeaderEnd>
        <PremiumButton onClick={navigateToPremiumIndex}>
          {t('premium.feed.buy-premium')}
        </PremiumButton>
        <BoostButton onClick={navigateToPremiumIndex}>
          {t('premium.button.buy-boosters')}
        </BoostButton>
        <Button
          size="h32"
          radius="rounded"
          variant="ghost"
          iconRight={<CloseIcon />}
          onClick={onClose}
        />
      </HeaderEnd>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: space-between;
  padding-inline: 12px;
`

const HeaderStart = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 10px;
`

const Title = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;

  font-size: 16px;
  font-weight: 600;
  line-height: 20px;

  & span {
    ${premiumFontCss}
  }
`

const HeaderEnd = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 8px;
  padding-block: 12px;
`

const ButtonBase = styled.button`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;

  height: 32px;
  border-radius: 8px;
  padding-inline: 14px;

  text-align: center;

  font-size: 11px;
  font-weight: 600;
  line-height: 16px; /* 145.455% */
`
const BoostButton = styled(ButtonBase)`
  border: 1px solid var(--boostButton-9);
  transition: all 0.15s ease-in-out;
  &:hover {
    box-shadow: inset 0 0 4px 0 var(--boostGlow-9);
  }
  ${boostersFontCss}
`
const PremiumButton = styled(ButtonBase)`
  border: 1px solid var(--subscriptionButton-9);
  transition: all 0.15s ease-in-out;
  &:hover {
    box-shadow: inset 0 0 4px 0 var(--subscriptionGlow-9);
  }
  ${premiumFontCss}
`

const PremiumFilledIcon = createIcon(
  <>
    <path
      d="M3.10193 14.0144C2.73709 11.6201 2.37205 9.22583 2.00681 6.83154C1.989 6.71389 2.0063 6.59355 2.0565 6.48584C2.1067 6.37814 2.18754 6.28796 2.28871 6.22678C2.38988 6.1656 2.50681 6.1362 2.6246 6.14232C2.74239 6.14843 2.8557 6.18979 2.95012 6.26113L6.36209 8.84313C6.45075 8.91026 6.55204 8.95843 6.65978 8.9847C6.76752 9.01097 6.87943 9.01479 6.98868 8.99591C7.09793 8.97704 7.20221 8.93587 7.29516 8.87493C7.3881 8.81399 7.46774 8.73456 7.52922 8.64149L10.3699 4.33976C10.4392 4.23523 10.5329 4.14953 10.6428 4.09027C10.7527 4.031 10.8754 4 11 4C11.1246 4 11.2473 4.031 11.3572 4.09027C11.4671 4.14953 11.5608 4.23523 11.6301 4.33976L14.4708 8.63907C14.5323 8.73213 14.6119 8.81157 14.7048 8.87251C14.7978 8.93345 14.9021 8.97461 15.0113 8.99349C15.1206 9.01236 15.2325 9.00855 15.3402 8.98228C15.448 8.95601 15.5493 8.90784 15.6379 8.84071L19.0499 6.25871C19.1443 6.18737 19.2576 6.14601 19.3754 6.1399C19.4932 6.13378 19.6101 6.16318 19.7113 6.22436C19.8125 6.28554 19.8933 6.37572 19.9435 6.48342C19.9937 6.59112 20.011 6.71147 19.9932 6.82912C19.6279 9.22341 19.2629 11.6177 18.8981 14.012L3.10193 14.0144Z"
      fill="url(#paint0_linear_22066_16214)"
    />
    <path
      d="M3.10284 15.3429L18.9002 15.3429V17.1686C18.9002 17.2778 18.8789 17.3859 18.8375 17.4868C18.7961 17.5876 18.7354 17.6793 18.6589 17.7565C18.5824 17.8337 18.4915 17.8949 18.3916 17.9367C18.2916 17.9785 18.1845 18 18.0763 18H3.92673C3.70822 18 3.49866 17.9124 3.34415 17.7565C3.18964 17.6006 3.10284 17.3891 3.10284 17.1686V15.3429Z"
      fill="url(#paint1_linear_22066_16214)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_22066_16214"
        x1="11"
        y1="4"
        x2="11"
        y2="18"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--subscriptionGradientFrom-9)" />
        <stop offset="1" stopColor="var(--subscriptionGradientTo-9)" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_22066_16214"
        x1="11"
        y1="4"
        x2="11"
        y2="18"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--subscriptionGradientFrom-9)" />
        <stop offset="1" stopColor="var(--subscriptionGradientTo-9)" />
      </linearGradient>
    </defs>
  </>,
  '0 0 22 22',
)
